import React, { useState, useEffect } from "react";
import { TEAM_CATEGORIES } from "../services/azureTeamService";

const TeamMemberModal = ({ member, employees, projects = [], onSave, onClose, onImageDelete, loading, canManageTeam }) => {
  const isNew = !member;

  const [formData, setFormData] = useState({
    teamId: "",
    employeeUuid: "",
    nummer: "",
    name: "",
    email: "", // Login email for access control
    internalRate: 0,
    pensum: 100,
    start: "",
    ende: "",
    beschrieb: "",
    public: false,
    imTeam: true,
    kategorie: "",
    geschlecht: "",
    geburtsdatum: "",
    bildId: "",
    bildUrls: null,
    tempFile: null,
    // Freelancer-specific fields
    zugeordneteProjekte: [],
    // Children for FAK Zulagen
    kinder: [],
    // AbaNinja config
    noAbaNinja: false,
    // Onboarding costs (new member only)
    onboardingLaptop: false,
    onboardingLaptopBetrag: "",
    onboardingArchicad: false,
    onboardingArchicadBetrag: "",
  });

  // Check if current category is Freelancer
  const isFreelancer = formData.kategorie === "Freelancer";

  useEffect(() => {
    if (member) {
      setFormData({
        ...member,
        email: member.email || "",
        beschrieb: member.beschrieb || "",
        ende: member.ende || "",
        kategorie: member.kategorie || "",
        geschlecht: member.geschlecht || "",
        geburtsdatum: member.geburtsdatum || "",
        zugeordneteProjekte: member.zugeordneteProjekte || [],
        kinder: member.kinder || [],
        noAbaNinja: member.noAbaNinja || false,
        tempFile: null,
      });
    }
  }, [member]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmployeeSelect = (employeeUuid) => {
    const employee = employees.find((e) => e.uuid === employeeUuid);
    if (employee) {
      setFormData((prev) => ({
        ...prev,
        employeeUuid,
        name: `${employee.firstName} ${employee.lastName}`,
      }));
    } else {
      setFormData((prev) => ({ ...prev, employeeUuid }));
    }
  };

  // Handle project assignment toggle for freelancers
  const handleProjectToggle = (projectCode) => {
    setFormData((prev) => {
      const currentProjects = prev.zugeordneteProjekte || [];
      if (currentProjects.includes(projectCode)) {
        return {
          ...prev,
          zugeordneteProjekte: currentProjects.filter((code) => code !== projectCode),
        };
      } else {
        return {
          ...prev,
          zugeordneteProjekte: [...currentProjects, projectCode],
        };
      }
    });
  };

  // Kinder (children) handlers for FAK Zulagen
  const handleAddChild = () => {
    setFormData((prev) => ({
      ...prev,
      kinder: [...prev.kinder, { vorname: "", geburtsdatum: "", inAusbildung: false }],
    }));
  };

  const handleRemoveChild = (idx) => {
    setFormData((prev) => ({
      ...prev,
      kinder: prev.kinder.filter((_, i) => i !== idx),
    }));
  };

  const handleChildChange = (idx, field, value) => {
    setFormData((prev) => {
      const kinder = [...prev.kinder];
      kinder[idx] = { ...kinder[idx], [field]: value };
      return { ...prev, kinder };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.start) {
      return;
    }
    onSave(formData, isNew);
  };

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <div className="team-member-modal">
        <div className="modal-header">
          <h2>{isNew ? "Neues Teammitglied" : "Teammitglied bearbeiten"}</h2>
          <button className="btn-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label>Mitarbeiter (AbaNinja)</label>
              <select
                value={formData.employeeUuid}
                onChange={(e) => handleEmployeeSelect(e.target.value)}
              >
                <option value="">Manuell oder wählen...</option>
                {employees.map((emp) => (
                  <option key={emp.uuid} value={emp.uuid}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Nummer *</label>
              <input
                type="text"
                value={formData.nummer}
                onChange={(e) => handleChange("nummer", e.target.value)}
                placeholder="z.B. MF01"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Vor- und Nachname"
                required
              />
            </div>
            <div className="form-group">
              <label>Kategorie</label>
              <select
                value={formData.kategorie}
                onChange={(e) => handleChange("kategorie", e.target.value)}
              >
                <option value="">Keine Kategorie</option>
                {TEAM_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Geschlecht</label>
              <select
                value={formData.geschlecht}
                onChange={(e) => handleChange("geschlecht", e.target.value)}
              >
                <option value="">-</option>
                <option value="M">Maennlich</option>
                <option value="W">Weiblich</option>
              </select>
            </div>
            <div className="form-group">
              <label>Geburtsdatum</label>
              <input
                type="date"
                value={formData.geburtsdatum}
                onChange={(e) => handleChange("geburtsdatum", e.target.value)}
              />
            </div>
          </div>

          {/* Email field for access control - required for all team members */}
          <div className="form-group">
            <label>Login Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="vorname.nachname@malheur-fortuna.ch"
              title="Die E-Mail-Adresse für die Anmeldung"
            />
            <small className="form-hint">Die Azure AD E-Mail-Adresse für den Systemzugang</small>
          </div>

          {/* Freelancer-specific: Project Assignment */}
          {isFreelancer && projects.length > 0 && (
            <div className="form-group">
              <label>Projektzuordnung (Mehrfachauswahl)</label>
              <div className="project-assignment-grid">
                {projects.map((project) => (
                  <label key={project.id} className="project-checkbox-label">
                    <input
                      type="checkbox"
                      checked={(formData.zugeordneteProjekte || []).includes(project.code)}
                      onChange={() => handleProjectToggle(project.code)}
                    />
                    <span
                      className="project-color-indicator"
                      style={{ backgroundColor: project.color || "#6b7280" }}
                    />
                    <span className="project-code">{project.code}</span>
                    <span className="project-title">{project.title}</span>
                  </label>
                ))}
              </div>
              {(formData.zugeordneteProjekte || []).length > 0 && (
                <div className="assigned-projects-summary">
                  Zugeordnet: {(formData.zugeordneteProjekte || []).join(", ")}
                </div>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Start *</label>
              <input
                type="date"
                value={formData.start}
                onChange={(e) => handleChange("start", e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Ende</label>
              <input
                type="date"
                value={formData.ende}
                onChange={(e) => handleChange("ende", e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Pensum %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.pensum}
                onChange={(e) => handleChange("pensum", parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="form-group">
              <label>Interner Stundensatz (CHF)</label>
              <input
                type="number"
                value={formData.internalRate}
                onChange={(e) => handleChange("internalRate", parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Beschrieb</label>
            <textarea
              value={formData.beschrieb}
              onChange={(e) => handleChange("beschrieb", e.target.value)}
              placeholder="Rolle, Fähigkeiten..."
              rows={3}
            />
          </div>

          {/* Kinder section (FAK Zulagen) - only for non-freelancers with manage permission */}
          {canManageTeam && !isFreelancer && (
            <div className="kinder-section">
              <div className="kinder-header">
                <label className="section-label">Kinder</label>
                <button type="button" className="btn btn-sm btn-secondary" onClick={handleAddChild}>+ Kind</button>
              </div>
              {formData.kinder.map((kind, idx) => (
                <div key={idx} className="kinder-row">
                  <div className="form-group">
                    <label>Vorname</label>
                    <input
                      type="text"
                      value={kind.vorname}
                      onChange={(e) => handleChildChange(idx, "vorname", e.target.value)}
                      placeholder="Vorname"
                    />
                  </div>
                  <div className="form-group">
                    <label>Geburtsdatum</label>
                    <input
                      type="date"
                      value={kind.geburtsdatum}
                      onChange={(e) => handleChildChange(idx, "geburtsdatum", e.target.value)}
                    />
                  </div>
                  <div className="form-group checkbox-inline kinder-ausbildung">
                    <label>
                      <input
                        type="checkbox"
                        checked={kind.inAusbildung || false}
                        onChange={(e) => handleChildChange(idx, "inAusbildung", e.target.checked)}
                      />
                      In Ausbildung
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn-icon btn-delete"
                    onClick={() => handleRemoveChild(idx)}
                    title="Kind entfernen"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              ))}
              {formData.kinder.length === 0 && (
                <div className="kinder-empty">Keine Kinder erfasst</div>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group checkbox-inline">
              <label>
                <input
                  type="checkbox"
                  checked={formData.imTeam}
                  onChange={(e) => handleChange("imTeam", e.target.checked)}
                />
                Im Team
              </label>
            </div>
            <div className="form-group checkbox-inline">
              <label>
                <input
                  type="checkbox"
                  checked={formData.public}
                  onChange={(e) => handleChange("public", e.target.checked)}
                />
                Öffentlich (Website)
              </label>
            </div>
            <div className="form-group checkbox-inline">
              <label>
                <input
                  type="checkbox"
                  checked={formData.noAbaNinja}
                  onChange={(e) => handleChange("noAbaNinja", e.target.checked)}
                />
                Ohne AbaNinja
              </label>
              <small className="form-hint">Keine AbaNinja-Anbindung – Zeiterfassung nur in Table Storage</small>
            </div>
          </div>

          {/* Onboarding Costs (only when creating new member) */}
          {isNew && (
            <div className="onboarding-section">
              <label className="section-label">Onboarding-Kosten</label>
              <div className="onboarding-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.onboardingLaptop}
                    onChange={(e) => handleChange("onboardingLaptop", e.target.checked)}
                  />
                  Neuer Laptop
                </label>
                {formData.onboardingLaptop && (
                  <input
                    type="number"
                    className="onboarding-betrag"
                    value={formData.onboardingLaptopBetrag}
                    onChange={(e) => handleChange("onboardingLaptopBetrag", e.target.value)}
                    placeholder="Betrag CHF"
                  />
                )}
              </div>
              <div className="onboarding-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.onboardingArchicad}
                    onChange={(e) => handleChange("onboardingArchicad", e.target.checked)}
                  />
                  ArchiCad Lizenz
                </label>
                {formData.onboardingArchicad && (
                  <input
                    type="number"
                    className="onboarding-betrag"
                    value={formData.onboardingArchicadBetrag}
                    onChange={(e) => handleChange("onboardingArchicadBetrag", e.target.value)}
                    placeholder="Betrag CHF"
                  />
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Bild</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files[0]) {
                  handleChange("tempFile", e.target.files[0]);
                }
              }}
            />
            {formData.tempFile && (
              <div className="file-selected">
                Ausgewählt: {formData.tempFile.name}
              </div>
            )}
            {formData.bildUrls?.thumbnail && !formData.tempFile && (
              <div className="image-preview-container">
                <img
                  src={formData.bildUrls.thumbnail}
                  alt={formData.name}
                  className="image-preview-thumb"
                />
                {member && (
                  <button
                    type="button"
                    className="btn-icon btn-delete"
                    onClick={() => onImageDelete(member.teamId, formData.bildId)}
                    title="Bild löschen"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3,6 5,6 21,6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || !formData.name || !formData.start}>
              {loading ? "Speichern..." : isNew ? "Hinzufügen" : "Speichern"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default TeamMemberModal;
